<template>
  <div
    class="user-list-card"
  >
    <m-table
      :columns="columns"
      :data-source="rows"
      class="_table"
      :custom-row="handleRowClick"
      :pagination="{ pageSize: 50 }"
      rows-clickable
      show-count
      hide-vertical-border
    >
      <template #name="{ row }">
        <user-display
          :user="row"
          xl
          disable-tooltip
        >
          <template #subName>
            <div class="_subtitle">
              {{ row.email }}
            </div>
          </template>
        </user-display>
      </template>
      <template #status="{ row }">
        <user-status-badge
          :user="row"
          :users="props.entities"
        />
      </template>
      <template #userProvisioning="{ row }">
        <user-provisioning-status-badge :user="row" />
      </template>
      <template #space-property="{ row }">
        <template
          v-if="row.spaces.length === 0"
        >
          <div :style="{ color: $colors.grey.base }">
            -
          </div>
        </template>
        <template
          v-else-if="row.spaces.length === 1"
        >
          <div :style="{ display: 'flex' }">
            <m-tag
              :custom-color="{ color: $colors.grey.darken4, backgroundColor: getColor(row.spaces[0].color) }"
              :title="row.spaces[0].title"
              :icon="buildIconFromEntity(row.spaces[0])"
              :style="{ maxWidth: '15rem' }"
            />
          </div>
        </template>
        <m-dropdown
          v-else
          :title="pluralize(textByLang(spaceProperty.label, userLang), userLang)"
          @click.stop
        >
          <m-btn
            hide-border
            small
          >
            {{ row.spaces.length }} {{ pluralize(textByLang(spaceProperty.label, userLang), userLang) }}
            <m-icon
              :style="{ marginLeft: '.4rem' }"
              size="12"
              :color="$colors.grey.lighten1"
              type="down"
            />
          </m-btn>
          <template #overlay>
            <m-card
              list
              no-padding
            >
              <m-card-item
                v-for="space in row.spaces"
                :key="space.uid"
                :clickable="false"
              >
                <m-tag
                  :custom-color="{ color: $colors.grey.darken4, backgroundColor: getColor(space.color) }"
                  :title="space.title"
                  :icon="buildIconFromEntity(space)"
                />
              </m-card-item>
            </m-card>
          </template>
        </m-dropdown>
      </template>
      <template #accessGroups="{ accessGroups }">
        {{ accessGroups }}
      </template>
      <template #actions="{ row }">
        <div class="_actions">
          <m-dropdown
            :title="$t('general.actions')"
            close-on-click
            @click.stop
          >
            <m-btn
              icon="ellipsis"
              fab
              hide-border
              small
            />
            <template #overlay>
              <m-card
                class="_user-actions"
                no-padding
                list
              >
                <m-card-item
                  icon="edit"
                  hide-border
                  @click="editUser(row.uid)"
                >
                  {{ $t('general.edit') }}
                </m-card-item>
                <user-confirm-invite-action
                  v-if="row.status !== USER_STATUS_ACTIVE && row.status !== USER_STATUS_ARCHIVED"
                  :user="row"
                />
                <user-archive-action :user="row" />
                <user-provisioning-disable-action
                  v-if="provisionedUserLookup[row.uid] !== undefined"
                  :provisioned-user="provisionedUserLookup[row.uid]"
                />
                <m-card-item
                  icon="delete"
                  class="_delete-btn"
                  :disabled="row.uid === loggedInUser.uid"
                  hide-border
                  @click="confirmDelete(row)"
                >
                  {{ $t('general.delete') }}
                </m-card-item>
              </m-card>
            </template>
          </m-dropdown>
        </div>
      </template>
    </m-table>
    <m-drawer
      :title="$t('userListCard.editUser')"
      :value="showDrawer"
      @close="hide"
    >
      <edit-user-form
        :key="selectedUserId"
        v-model:dirty="modalDirty"
        :user="selectedUser"
        :account="props.account"
        show-cancel
        @update="hide"
        @create="hide"
        @cancel="hide"
        @go-to-access-groups="goToAccessGroups"
        @updated="showDrawer = false"
      />
    </m-drawer>
  </div>
</template>

<script setup>
import EditUserForm from '@/components/EditUserForm.vue';
import UserArchiveAction from '@/components/UserArchiveAction.vue';
import UserConfirmInviteAction from '@/components/UserConfirmInviteAction.vue';
import UserDisplay from 'shared/components/UserDisplay.vue';
import UserProvisioningDisableAction from '@/components/UserProvisioningDisableAction.vue';
import UserProvisioningStatusBadge from '@/components/UserProvisioningStatusBadge.vue';
import UserStatusBadge from '@/components/UserStatusBadge.vue';
import useAccessGroup from '@/composables/access/access-group';
import useConfirmDialog from '@/composables/confirm-dialog';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useProperties from '@/composables/property/property';
import useRepo from '@/nebula/repo';
import useSnackBar from '@/composables/snackbar';
import useUserProvisioning from '@/composables/user-provisioning/user-provisioning';
import useUsers from '@/composables/user/users';
import { USER_STATUS_ACTIVE, USER_STATUS_ARCHIVED } from '@/lib/constants';
import { buildIconFromEntity } from 'shared/lib/icon';
import { computed, ref } from 'vue';
import { getColor } from 'shared/lib/color-map';
import { pluralize, textByLang } from 'shared/lib/language';
import { propertyType, routeName } from 'shared/constants.json';
import { provisionedUser as provisionedUserConfig } from 'shared/api/query/configs.json';
import { sortChain } from 'shared/lib/sort';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { userListFilter } from '@/constants.json';

const props = defineProps({
  account: {
    type: Object,
    required: true,
  },
  entities: {
    type: Array,
    default: () => [],
  },
  search: {
    type: String,
    default: '',
  },
  filter: {
    type: String,
    default: userListFilter.active,
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const accessGroupSvc = useAccessGroup();
const { loggedInUser, userLang } = useLoggedInUser();
const { deleteUser } = useUsers();
const { spaceProperty } = useProperties();
const provisionedUserRepo = useRepo(provisionedUserConfig.model);
const userProvisioningRepo = useUserProvisioning();

const showDrawer = ref(false);
const selectedUser = ref(null);
const modalDirty = ref(false);

const selectedUserId = computed(() => {
  if (selectedUser.value === null) {
    return 0;
  }
  return selectedUser.value.uid;
});

const provisionedUserLookup = computed(() => Object.fromEntries(props.entities.map((u) => [u.uid, provisionedUserRepo.entityList.value.find((pu) => pu.externalId === u.externalId)])));

const columns = computed(() => {
  const columns = [
    {
      key: 'name',
      dataIndex: 'name',
      title: t('userListCard.name'),
      sorter: sorter('firstName'),
      scopedSlots: { customRender: 'name' },
    },
  ];

  if (!store.state.breakpoint.smAndDown) {
    columns.push({
      key: 'status',
      dataIndex: 'status',
      title: t('userList.status'),
      sorter: sorter('status'),
      scopedSlots: { customRender: 'status' },
    });

    if (userProvisioningRepo.userProvisioning.value !== undefined && userProvisioningRepo.userProvisioning.value.pausedAt === null) {
      columns.push({
        key: 'userProvisioning',
        dataIndex: 'provisioned',
        title: t('userList.userProvisioning'),
        sorter: (a, b) => {
          const provisionedUserA = userProvisioningRepo.findProvisionedUser(a);
          const provisionedUserB = userProvisioningRepo.findProvisionedUser(b);

          const findValue = (provisionedUser) => {
            if (provisionedUser === undefined) {
              return 0;
            }

            if (provisionedUser.deactivatedAt !== null) {
              return 1;
            }

            return 2;
          };

          return findValue(provisionedUserA) - findValue(provisionedUserB);
        },
        scopedSlots: { customRender: 'userProvisioning' },
        onFilter: (value, record) => {
          const provisionedUser = userProvisioningRepo.findProvisionedUser(record);

          if (t('userProvisioningStatusBadge.enabled').toLowerCase().includes(value.toLowerCase())) {
            return provisionedUser !== undefined && provisionedUser.deactivatedAt === null;
          }

          if (t('userProvisioningStatusBadge.disabled').toLowerCase().includes(value.toLowerCase())) {
            return provisionedUser !== undefined && provisionedUser.deactivatedAt !== null;
          }

          if (t('userProvisioningStatusBadge.notAvailable').toLowerCase().includes(value.toLowerCase())) {
            return provisionedUser === undefined;
          }

          return false;
        },
      });
    }

    if (spaceProperty.value !== undefined) {
      columns.push({
        key: 'spaces',
        dataIndex: 'spaces',
        title: textByLang(spaceProperty.value.label, userLang.value),
        scopedSlots: { customRender: 'space-property' },
      });
    }

    columns.push({
      key: 'accessGroups',
      dataIndex: 'accessGroups',
      title: t('userList.accessGroups'),
      sorter: sortChain([sorter('accessGroups'), sorter('firstName')]),
      scopedSlots: { customRender: 'accessGroups' },
    });
  }

  columns.push({
    key: 'actions',
    dataIndex: 'actions',
    title: '',
    width: '30',
    scopedSlots: { customRender: 'actions' },
  });

  return columns;
});

const rows = computed(() => {
  const filteredBySearchRows = props.entities.filter((u) => {
    switch (props.filter) {
      case userListFilter.active:
        if (u.archivedAt !== null) {
          return false;
        }
        break;
      case userListFilter.archived:
        if (u.archivedAt === null) {
          return false;
        }
        break;
      case userListFilter.none:
      default:
        break;
    }

    if (props.search === '') {
      return true;
    }
    const s = props.search.toLowerCase();
    return u.firstName.toLowerCase().includes(s) || u.lastName.toLowerCase().includes(s) || u.email.toLowerCase().includes(s);
  });

  const mayRestore = (user) => {
    if (provisionedUserLookup.value[user.uid] === undefined) {
      return true;
    }

    return provisionedUserLookup.value[user.uid].deactivatedAt !== null;
  };

  return filteredBySearchRows.map((user) => {
    let values = [];
    const accessGroups = getAccessGroups(user);
    if (user.values !== undefined) {
      values = user.values.filter((v) => v.property.type === propertyType.space);
    }
    if (values.length === 0) {
      return {
        ...user,
        accessGroups,
        spaces: [],
        mayRestore: mayRestore(user),
      };
    }

    return {
      ...user,
      accessGroups,
      spaces: values[0].spaces,
      mayRestore: mayRestore(user),
    };
  });
});

const goToAccessGroups = () => {
  const next = () => router.push({ name: routeName.accessGroupList });
  hide(next);
};

const hide = (next = () => {}) => {
  const hideFn = () => {
    showDrawer.value = false;
    next();
  };

  if (!modalDirty.value) {
    hideFn();
    return;
  }

  const title = t('general.discardEditPrompt');
  const okText = t('general.yesDiscard');
  const cancelText = t('general.close');
  confirmDialog.confirm({
    title,
    okText,
    okType: 'danger',
    cancelText,
    maskClosable: true,
    onOk() {
      hideFn();
    },
  });
};

const getAccessGroups = (user) => {
  const res = accessGroupSvc.entityList.value
    .filter((ag) => ag.accessPolicy.scopes.some((next) => !!(next.scopeUsers.map((u) => u.uid).includes(user.uid))))
    .map((ag) => ag.name);

  if (res.length === 0) {
    res.push(t('accessGroupForm.defaultGroupTitle'));
  }
  return res.join(', ');
};

const editUser = (userId) => {
  selectedUser.value = rows.value.find((user) => user.uid === userId);
  showDrawer.value = true;
};

const sorter = (key) => (a, b) => {
  switch (typeof a[key]) {
    case 'boolean':
      if (a[key] && b[key]) {
        return 0;
      }
      if (a[key] && !b[key]) {
        return -1;
      }
      if (!a[key] && b[key]) {
        return 1;
      }
      return a[key] && b[key];
    default:
      return (a, b) => a[key].localeCompare(b[key]);
  }
};

const snackbar = useSnackBar();
const confirmDialog = useConfirmDialog();

const confirmDelete = (user) => {
  const deleteMethod = () => deleteUser(user).then(() => {
    snackbar.success(t('success.deleted'));
  }).catch(() => {
    snackbar.error(t('error.default'));
  });

  confirmDialog.confirm({
    title: t('userList.deleteUserTitle'),
    okText: t('general.yesDelete'),
    okType: 'danger',
    cancelText: t('general.cancel'),
    maskClosable: true,
    onOk() {
      deleteMethod();
    },
  });
};

const handleRowClick = (record) => ({
  on: {
    click: () => {
      editUser(record.uid);
    },
  },
});
</script>

<style
    lang="scss"
    type="text/scss"
>
  ._subtitle {
    font-size: $font-size-4;
    color: $font-color-secondary;
  }

  ._invited-by-at {
    font-size: $font-size-2;
  }

  ._status {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
</style>
